/**
 * editor2/hooks/useMemoTimeline.ts
 * メモのタイムラインパース・編集ロジック
 * ReservationEditorPanel.tsx の renderMemoTimeline / handleSaveBlockEdit から抽出
 */
import { useCallback } from 'react';
import type { MemoBlock } from '../types';
import { ACTION_LABELS } from '../constants';
import { getStaffName, formatTimestamp } from './useAutoChangeMemo';

// メモヘッダーの正規表現: ---タイトル YYYY-MM-DD HH:MM---
const MEMO_HEADER_REGEX = /^---(.*?) (\d{4}-\d{2}-\d{2} \d{2}:\d{2})---$/;

/**
 * メモテキストを MemoBlock 配列にパースする
 */
export function parseMemoBlocks(memo: string): MemoBlock[] {
  if (!memo) return [];

  const lines = memo.split('\n');
  const items: MemoBlock[] = [];
  let currentBlock: {
    type: 'event';
    title: string;
    time: string;
    lines: string[];
    originalIdx: number;
  } | null = null;
  let freeText: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(MEMO_HEADER_REGEX);

    if (match) {
      // ヘッダーが見つかった: フリーテキストがあればまずpush
      if (freeText.length > 0) {
        const txt = freeText.join('\n').trim();
        if (txt) {
          items.push({ type: 'text', content: txt, originalIdx: items.length });
        }
        freeText = [];
      }
      currentBlock = {
        type: 'event',
        title: match[1],
        time: match[2],
        lines: [],
        originalIdx: items.length,
      };
    } else if (currentBlock) {
      if (line === '---') {
        // ブロック終端
        const txt = currentBlock.lines.join('\n').trim();
        items.push({
          type: 'event',
          title: currentBlock.title,
          time: currentBlock.time,
          content: txt,
          originalIdx: currentBlock.originalIdx,
        });
        currentBlock = null;
      } else {
        currentBlock.lines.push(line);
      }
    } else {
      freeText.push(line);
    }
  }

  // 未閉じブロックの処理
  if (currentBlock) {
    items.push({
      type: 'event',
      title: currentBlock.title,
      time: currentBlock.time,
      content: currentBlock.lines.join('\n').trim(),
      originalIdx: currentBlock.originalIdx,
    });
  } else if (freeText.length > 0) {
    const txt = freeText.join('\n').trim();
    if (txt) {
      items.push({ type: 'text', content: txt, originalIdx: items.length });
    }
  }

  return items;
}

/**
 * MemoBlock 配列を一つのメモ文字列に再構築する
 */
export function blocksToMemo(blocks: MemoBlock[]): string {
  let memo = '';
  blocks.forEach((item) => {
    if (item.type === 'text') {
      memo += (memo ? '\n' : '') + item.content;
    } else {
      memo +=
        (memo ? '\n' : '') +
        `---${item.title} ${item.time}---` +
        (item.content ? '\n' + item.content : '') +
        '\n---';
    }
  });
  return memo;
}

/**
 * 特定のブロックの内容を更新して新しいメモ文字列を返す
 */
export function updateMemoBlock(
  memo: string,
  index: number,
  newContent: string,
): string {
  const blocks = parseMemoBlocks(memo);
  if (blocks[index]) {
    blocks[index] = { ...blocks[index], content: newContent };
  }
  return blocksToMemo(blocks);
}

/**
 * メモにアクションエントリを追記する
 */
export function appendMemoAction(
  memo: string,
  actionType: string,
  content?: string,
  staffName?: string,
): string {
  const ts = formatTimestamp();
  const name = staffName || getStaffName();
  const actionLabel = ACTION_LABELS[actionType] || ACTION_LABELS.other;

  const header = `---${actionLabel} ${name} ${ts}---`;
  const footer = '---';
  const trimmedContent = content?.trim();
  const newEntry = trimmedContent
    ? `${header}\n${trimmedContent}\n${footer}`
    : `${header}\n${footer}`;

  return memo ? `${memo}\n${newEntry}` : newEntry;
}

/**
 * React hook: useMemoTimeline
 */
export default function useMemoTimeline() {
  const parse = useCallback((memo: string) => parseMemoBlocks(memo), []);

  const update = useCallback(
    (memo: string, index: number, newContent: string) =>
      updateMemoBlock(memo, index, newContent),
    [],
  );

  const append = useCallback(
    (memo: string, actionType: string, content?: string, staffName?: string) =>
      appendMemoAction(memo, actionType, content, staffName),
    [],
  );

  return {
    parseMemoBlocks: parse,
    updateMemoBlock: update,
    appendMemoAction: append,
    blocksToMemo,
  };
}
